import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import type { PortalRole } from "@/types/domain";
import { buttonClass } from "@/components/base/buttonStyles";
import ToastLayer from "@/components/base/ToastLayer";

/**
 * 한중 밖 주체가 쓰는 포털의 셸 — 업체(`/carrier`)와 청구업체(`/client`)가 함께 쓴다.
 *
 * 계약 §4.1·아키텍처 절이 채널을 "담당자 웹, 기사 모바일 웹, **하도급업체 웹**" 셋으로
 * 적고, 2026-09-23 에 **청구업체(매출처)** 가 넷째로 들어왔다.
 *
 * **기사 앱과 달리 폭을 고정하지 않는다.** 기사는 자기 차 한 대를 폰으로 보지만, 업체
 * 배차 담당자는 차 여러 대와 여러 구간을 **사무실 PC** 로 대조한다(사용자 결정 2026-09-07).
 * 매출처 경리도 같다 — 월 수백 장을 폰으로 받지는 않는다. 그래도 현장에서 폰으로 열 수
 * 있어야 해서, 탭을 상단에 두고 본문만 넓힌다 — 하단 고정 탭은 넓은 화면에서 갈 곳 없이
 * 떠 보인다.
 *
 * 둘이 다른 것은 **제목·탭·로그아웃 목적지 셋뿐**이라 셸을 복제하지 않았다(2026-10-01).
 * 복제하면 아래 결정 — 어느 거래처로 로그인했는지 늘 보여 준다 — 이 한쪽에만 남는다.
 *
 * **그 셋도 이제 스스로 정한다** (고객 질문 2026-10-15). 한 계정이 운송업체·청구업체
 * 기능을 함께 켤 수 있게 되면서 탭이 **계정마다 달라졌다** — 바깥에서 고정 목록을
 * 넘겨받으면 둘 다 켠 계정이 자기가 쓸 수 있는 메뉴의 절반만 본다. 고객이 겪은
 * *"청구업체로 계정을 만들었는데 송장 제출 메뉴가 없다"* 가 바로 그 증상이다.
 */
export interface PortalTab {
  label: string;
  path: string;
}

/**
 * 기능별 탭. **`내 정보` 는 여기 없다** — 기능이 몇 개든 한 번만 서야 해서 아래에서 붙인다.
 *
 * 경로는 기능마다 그대로 둔다(`/carrier/*` · `/client/*`). 한 묶음으로 합치면 라우트
 * 가드·북마크·옛 링크가 전부 따라 움직여야 하는데, 얻는 것은 주소창 글자뿐이다.
 */
const TABS_BY_ROLE: Record<PortalRole, readonly PortalTab[]> = {
  // 업체가 하는 일은 셋이다 — 지시 확인 · 차량 배정(받은 배차 안) · 송장 업로드
  업체: [
    { label: "받은 배차", path: "/carrier/orders" },
    { label: "송장", path: "/carrier/invoice" },
  ],
  // 매출처가 하는 일은 하나다 — 자기 청구분의 사진을 보고 받아 간다. 올리지도 배정하지도 않는다
  청구업체: [{ label: "운송장 사진", path: "/client/invoices" }],
};

/** 기능 집합 → 머리말. 둘 다면 둘을 적는다 — 어느 쪽으로 들어왔든 같은 화면이다. */
function titleFor(roles: readonly PortalRole[]): string {
  if (roles.length > 1) return "한중 상하차 — 운송·청구업체";
  if (roles[0] === "청구업체") return "한중 상하차 — 청구업체";
  return "한중 상하차 — 업체";
}

export default function PortalLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { displayName, user, logout, portalRoles } = useAuth();

  const roles = portalRoles.length > 0 ? portalRoles : (["업체"] as PortalRole[]);
  const title = titleFor(roles);
  /**
   * 로그아웃 뒤 갈 곳은 **대표 기능**의 로그인 화면이다. 두 화면이 같은
   * `POST /api/auth/login` 을 쓰므로 어느 쪽으로 쳐도 들어오고, 돌아갈 자리가 하나면
   * 다음 로그인이 헷갈리지 않는다.
   */
  const loginPath = roles[0] === "청구업체" ? "/client/login" : "/carrier/login";
  const tabs: PortalTab[] = [
    ...roles.flatMap((role) => [...TABS_BY_ROLE[role]]),
    // `내 정보` 는 두 포털에서 같은 화면이다(`PortalProfilePage`) — 대표 기능 쪽 경로로 하나만 둔다
    { label: "내 정보", path: roles[0] === "청구업체" ? "/client/profile" : "/carrier/profile" },
  ];

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const handleLogout = async () => {
    await logout();
    navigate(loginPath, { replace: true });
  };

  return (
    <div className="flex h-screen flex-col bg-white">
      <header className="shrink-0 border-b border-gray-100">
        {/*
          좁은 화면에서는 두 줄로 접힌다(`flex-wrap`). 접지 않으면 제목이 **단어 중간에서
          끊기고** 거래처명과 사람 이름이 서로 밀어낸다 — 390px 에서 실제로 그랬다.
          제목은 끊지 않고(`whitespace-nowrap`), 긴 거래처명만 잘라 준다.
        */}
        <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center justify-between gap-x-3 gap-y-1 px-5 pb-3 pt-5">
          <div className="flex min-w-0 items-baseline gap-2">
            <h1 className="whitespace-nowrap text-sm font-semibold tracking-tight text-gray-900">
              {title}
            </h1>
            {/* 어느 거래처로 로그인했는지 늘 보여야 한다. 여러 곳을 대행하는 사람이 있다 */}
            {user?.company_name && (
              <span className="truncate text-xs text-gray-500" title={user.company_name}>
                {user.company_name}
              </span>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <span className="max-w-[9rem] truncate text-xs text-gray-500" title={displayName}>
              {displayName}
            </span>
            <button
              type="button"
              onClick={handleLogout}
              className={buttonClass("secondary", "small")}
            >
              로그아웃
            </button>
          </div>
        </div>
        <nav className="mx-auto flex w-full max-w-6xl items-center gap-6 px-5">
          {tabs.map((tab) => {
            const active = isActive(tab.path);
            return (
              <Link
                key={tab.path}
                to={tab.path}
                className={`-mb-px border-b-2 pb-2 text-sm transition-colors ${
                  active
                    ? "border-primary-600 font-medium text-primary-700"
                    : "border-transparent text-gray-400 hover:text-gray-700"
                }`}
              >
                {tab.label}
              </Link>
            );
          })}
        </nav>
      </header>

      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-6xl px-5 py-5">
          <Outlet />
        </div>
      </main>
      <ToastLayer />
    </div>
  );
}
